import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import MuiTooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import chesiLogo from '../assets/chesi-logo.png';
import './PickListDashboard.css';

const POLL_MS = 2800;

function heatClass(pct) {
  const n = Number(pct) || 0;
  if (n >= 90) return 'pld-heat-hi';
  if (n >= 80) return 'pld-heat-midhi';
  if (n >= 70) return 'pld-heat-mid';
  if (n >= 50) return 'pld-heat-low';
  return 'pld-heat-bad';
}

function formatIssued(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

const LIVE_PICK_WARN_MINUTES = 180; // highlight Time cell when over 3 hours

/** Row styling for Pick Time Bucket (matches wallboard tiers). */
function pickTimeBucketRowClass(label) {
  const key = String(label || '').trim();
  const map = {
    '15-30 min': 'pld-bucket-row-green',
    '31-60 min': 'pld-bucket-row-peach',
    '1-2 Hours': 'pld-bucket-row-red',
    'Over 2 Hours': 'pld-bucket-row-red',
    '1-2 Days': '',
  };
  return map[key] || '';
}

function elapsedMinutesSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
}

function formatDurationFromUpdated(iso) {
  if (!iso) return '—';
  const m = elapsedMinutesSince(iso);
  if (m == null) return '—';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h${m % 60}m`;
}

function DonutPair({ pickPct, linePct }) {
  const complete = Math.min(100, Math.max(0, pickPct));
  const rest = Math.max(0, 100 - complete);
  const dataPick = [
    { name: 'COMPLETE', value: complete, fill: '#2e7d32' },
    { name: 'UNASSIGNED', value: rest, fill: '#c62828' },
  ];
  const lc = Math.min(100, Math.max(0, linePct));
  const lr = Math.max(0, 100 - lc);
  const dataLine = [
    { name: 'COMPLETE', value: lc, fill: '#2e7d32' },
    { name: 'UNASSIGNED', value: lr, fill: '#c62828' },
  ];

  return (
    <div className="pld-donuts">
      <div className="pld-donut">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={dataPick} dataKey="value" cx="50%" cy="50%" innerRadius="62%" outerRadius="82%" paddingAngle={0} startAngle={90} endAngle={-270}>
              {dataPick.map((e, i) => (
                <Cell key={i} fill={e.fill} stroke="#333" strokeWidth={1} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pld-donut-center">{complete}%</div>
        <div className="pld-donut-caption">Fill By Pick Lists</div>
      </div>
      <div className="pld-donut">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={dataLine} dataKey="value" cx="50%" cy="50%" innerRadius="62%" outerRadius="82%" paddingAngle={0} startAngle={90} endAngle={-270}>
              {dataLine.map((e, i) => (
                <Cell key={i} fill={e.fill} stroke="#333" strokeWidth={1} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pld-donut-center">{lc}%</div>
        <div className="pld-donut-caption">Fill By Part Lines</div>
      </div>
      <div className="pld-donut-legend">
        <span>
          <span className="pld-dot" style={{ background: '#2e7d32' }} />
          COMPLETE
        </span>
        <span>
          <span className="pld-dot" style={{ background: '#c62828' }} />
          UNASSIGNED
        </span>
      </div>
    </div>
  );
}

function requestFs(el) {
  if (!el) return;
  if (el.requestFullscreen) return el.requestFullscreen();
  if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
}

function exitFs() {
  if (document.fullscreenElement || document.webkitFullscreenElement) {
    if (document.exitFullscreen) return document.exitFullscreen();
    if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  }
}

const PickListDashboard = () => {
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();
  const fsRef = useRef(null);
  const headerLogoUrlRef = useRef(null);
  const [data, setData] = useState(null);
  const dataRef = useRef(null);
  const [error, setError] = useState('');
  const [pollWarning, setPollWarning] = useState('');
  const [lastAt, setLastAt] = useState(null);
  const [isFs, setIsFs] = useState(false);
  const [headerLogoUrl, setHeaderLogoUrl] = useState(null);

  const loadHeaderLogo = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/settings/logo');
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        if (headerLogoUrlRef.current) URL.revokeObjectURL(headerLogoUrlRef.current);
        headerLogoUrlRef.current = url;
        setHeaderLogoUrl(url);
      } else {
        if (headerLogoUrlRef.current) URL.revokeObjectURL(headerLogoUrlRef.current);
        headerLogoUrlRef.current = null;
        setHeaderLogoUrl(null);
      }
    } catch {
      if (headerLogoUrlRef.current) URL.revokeObjectURL(headerLogoUrlRef.current);
      headerLogoUrlRef.current = null;
      setHeaderLogoUrl(null);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadHeaderLogo();
    return () => {
      if (headerLogoUrlRef.current) URL.revokeObjectURL(headerLogoUrlRef.current);
    };
  }, [loadHeaderLogo]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const load = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/dashboard/pick-lists');
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = json?.message || 'Failed to load dashboard';
        const hasCache = dataRef.current != null;
        if (res.status === 401 || res.status === 403) {
          setError(msg);
          setPollWarning('');
          return;
        }
        if (hasCache && res.status >= 500) {
          setPollWarning(msg);
          return;
        }
        throw new Error(msg);
      }
      setError('');
      setPollWarning('');
      setData(json);
      setLastAt(new Date());
    } catch (e) {
      if (dataRef.current != null) {
        setPollWarning(e.message || 'Refresh failed.');
      } else {
        setError(e.message || 'Failed to load');
      }
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    const onFs = () => {
      const el = fsRef.current;
      const active = document.fullscreenElement === el || document.webkitFullscreenElement === el;
      setIsFs(!!active);
    };
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = fsRef.current;
    if (!el) return;
    if (document.fullscreenElement === el || document.webkitFullscreenElement === el) {
      exitFs();
    } else {
      requestFs(el);
    }
  }, []);

  const chartRows = useMemo(() => {
    const rows = data?.byPicker;
    if (rows && rows.length) {
      return rows.map((r) => ({
        name: r.displayName || 'Picker',
        picks: r.picks,
        lines: r.lines,
      }));
    }
    return [{ name: '—', picks: 0, lines: 0 }];
  }, [data]);

  const highlightIdx = useMemo(() => {
    if (!data?.byPicker?.length) return -1;
    let max = -1;
    let idx = 0;
    data.byPicker.forEach((r, i) => {
      if (r.picks > max) {
        max = r.picks;
        idx = i;
      }
    });
    return idx;
  }, [data]);

  const kpis = data?.kpis;
  const fillRates = data?.fillRates || { byPickListsPct: 0, byPartLinesPct: 0 };

  return (
    <Layout>
      <div ref={fsRef} className="pick-dashboard-fs-root">
        {isFs && (
          <div className="pld-fs-branded-header">
            <div className="pld-fs-branded-left">
              <button
                type="button"
                className="pld-fs-chesi-wrap"
                onClick={() => navigate('/')}
                aria-label="Home"
              >
                <img src={chesiLogo} alt="Chesi" className="pld-fs-chesi-img" />
              </button>
              <div className="pld-fs-tenant-pill">
                {headerLogoUrl ? (
                  <img src={headerLogoUrl} alt="Tenant company logo" className="pld-fs-tenant-img" />
                ) : (
                  <span className="pld-fs-tenant-placeholder" aria-hidden />
                )}
              </div>
            </div>
          </div>
        )}
        <div className="pick-dashboard-outer">
          <div className="pld-toolbar">
            <span className="pld-pulse">
              <span className="pld-pulse-dot" aria-hidden />
              Live
            </span>
            {lastAt && (
              <span className="pld-live-meta">
                Updated {lastAt.toLocaleTimeString()}
                {data?.generatedAt && ` · ${new Date(data.generatedAt).toLocaleTimeString()} server`}
              </span>
            )}
            <MuiTooltip title={isFs ? 'Exit full screen' : 'Full screen'}>
              <IconButton
                size="small"
                onClick={toggleFullscreen}
                aria-label={isFs ? 'Exit full screen' : 'Enter full screen'}
                className="pld-fs-btn"
              >
                {isFs ? <CloseIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </MuiTooltip>
          </div>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {pollWarning && (
          <Alert severity="warning" onClose={() => setPollWarning('')}>
            {pollWarning} Showing last loaded data.
          </Alert>
        )}

        <div className="pick-dashboard-banner">PICK LISTS DASHBOARD</div>

        <div className="pick-dashboard-kpis">
          <div className="pld-kpi pld-kpi-navy">
            <div className="pld-kpi-label">TOTAL PICKS</div>
            <div className="pld-kpi-value">{kpis?.totalPicks ?? '—'}</div>
          </div>
          <div className="pld-kpi pld-kpi-teal">
            <div className="pld-kpi-label">TOTAL LINES</div>
            <div className="pld-kpi-value">{kpis?.totalLines ?? '—'}</div>
          </div>
          <div className="pld-kpi pld-kpi-white-green">
            <div className="pld-kpi-label">COMPLETE PICKS</div>
            <div className="pld-kpi-value">{kpis?.completePicks ?? '—'}</div>
          </div>
          <div className="pld-kpi pld-kpi-white-blue">
            <div className="pld-kpi-label">ACTIVE PICKS</div>
            <div className="pld-kpi-value">{kpis?.activePicks ?? '—'}</div>
          </div>
          <div className="pld-kpi pld-kpi-fail">
            <div className="pld-kpi-label">FAILED PICKS</div>
            <div className="pld-kpi-value">{kpis?.failedPicks ?? 0}</div>
          </div>
          <div className="pld-kpi pld-kpi-darkfill">
            <div className="pld-kpi-label">% FULFILLMENT</div>
            <div className="pld-kpi-value">{kpis?.fulfillmentPct ?? 0}%</div>
          </div>
          <div className="pld-kpi pld-kpi-grey">
            <div className="pld-kpi-label">ACTIVE PICKERS</div>
            <div className="pld-kpi-value">{kpis?.activePickers ?? 0}</div>
          </div>
          <div className="pld-kpi pld-kpi-wip">
            <div className="pld-kpi-wip-title">YESTERDAY&apos;S WIP</div>
            <div className="pld-kpi-wip-row">
              <div className="pld-kpi-wip-box red">
                {kpis?.yesterday?.pending ?? '—'}
                <span className="pld-kpi-wip-sub">Pending picks</span>
              </div>
              <div className="pld-kpi-wip-box green">
                {kpis?.yesterday?.completed ?? '—'}
                <span className="pld-kpi-wip-sub">Completed picks</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pick-dashboard-body">
          <div className="pld-panel">
            <div className="pld-panel-title">ORDER SUMMARY BY CUSTOMER</div>
            <div className="pld-table-wrap">
              <table className="pld-table">
                <thead>
                  <tr>
                    <th>CUSTOMER</th>
                    <th>PICKS</th>
                    <th>COMPLETE</th>
                    <th>LIVE</th>
                    <th>UNASSIGNED</th>
                    <th>FILL 1 (%)</th>
                    <th>LINES</th>
                    <th>COMPLETE</th>
                    <th>FAILED</th>
                    <th>FILL 2 (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.byCustomer?.length ? data.byCustomer : []).map((row) => (
                    <tr key={row.customer}>
                      <td>{row.customer}</td>
                      <td>{row.picks}</td>
                      <td>{row.complete}</td>
                      <td>{row.live}</td>
                      <td>{row.unassigned}</td>
                      <td className={heatClass(row.fillPickPct)}>{row.fillPickPct}%</td>
                      <td>{row.lines}</td>
                      <td>{row.linesComplete}</td>
                      <td>{row.linesFailed}</td>
                      <td className={heatClass(row.fillLinePct)}>{row.fillLinePct}%</td>
                    </tr>
                  ))}
                  {(!data?.byCustomer || data.byCustomer.length === 0) && (
                    <tr>
                      <td colSpan={10} style={{ textAlign: 'center', color: '#757575' }}>
                        No orders in pending, active, or dispatched yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pld-mid-stack">
            <div className="pld-chart-box">
              <div className="pld-panel-title">Today Picks By Picker</div>
              <div className="pld-chart-inner">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartRows} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="2 2" stroke="#bdbdbd" />
                    <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-16} textAnchor="end" height={42} />
                    <YAxis yAxisId="left" tick={{ fontSize: 9 }} width={28} allowDecimals={false} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9 }} width={28} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar yAxisId="left" dataKey="picks" name="# Picks" fill="#1565c0">
                      {chartRows.map((_, i) => (
                        <Cell key={i} fill={i === highlightIdx && highlightIdx >= 0 ? '#c62828' : '#1565c0'} />
                      ))}
                    </Bar>
                    <Line yAxisId="right" type="monotone" dataKey="lines" name="# Lines" stroke="#ff9800" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <table className="pld-summary-table pld-summary-below-chart">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th># Picks</th>
                    <th># Lines</th>
                  </tr>
                </thead>
                <tbody>
                  {chartRows.map((r) => (
                    <tr key={r.name}>
                      <td>{r.name}</td>
                      <td>{r.picks}</td>
                      <td>{r.lines}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pld-mid-row">
              <div className="pld-panel pld-mini-table">
                <div className="pld-panel-title">Today&apos;s Picks By Order Type</div>
                <div className="pld-table-wrap">
                  <table className="pld-table">
                    <thead>
                      <tr>
                        <th>ORDER TYPE</th>
                        <th>COMPLETE</th>
                        <th>UNASSIGNED</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.byOrderType || []).map((r) => (
                        <tr key={r.orderType}>
                          <td>{r.orderType}</td>
                          <td>{r.complete}</td>
                          <td>{r.unassigned}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="pld-panel pld-fill-rates-panel">
                <div className="pld-panel-title">Fill Rates</div>
                <DonutPair pickPct={fillRates.byPickListsPct} linePct={fillRates.byPartLinesPct} />
              </div>
            </div>

            <div className="pld-panel pld-mini-table">
              <div className="pld-panel-title">Pick Time Bucket</div>
              <div className="pld-table-wrap">
                <table className="pld-table">
                  <thead>
                    <tr>
                      <th>Bucket</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.timeBuckets || []).map((b) => (
                      <tr key={b.label} className={pickTimeBucketRowClass(b.label)}>
                        <td>{b.label}</td>
                        <td>{b.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="pld-mid-stack pld-right-stack">
            <div className="pld-panel">
              <div className="pld-panel-title">LIVE PICKINGS - ASSIGNED</div>
              <div className="pld-table-wrap">
                <table className="pld-table">
                  <thead>
                    <tr>
                      <th>DOC NO</th>
                      <th>CUST</th>
                      <th>PICKER</th>
                      <th>ORDER TYPE</th>
                      <th>LINES</th>
                      <th>Issued</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.liveAssigned || []).map((r) => {
                      const elapsed = elapsedMinutesSince(r.updatedAt);
                      const timeOver3h = elapsed != null && elapsed > LIVE_PICK_WARN_MINUTES;
                      return (
                      <tr key={r.id}>
                        <td>{r.docNo}</td>
                        <td>{r.cust}</td>
                        <td>{r.picker || '—'}</td>
                        <td>{r.orderType}</td>
                        <td>{r.lineCount}</td>
                        <td>{formatIssued(r.issuedAt)}</td>
                        <td className={timeOver3h ? 'pld-time-over-threshold' : undefined}>
                          {formatDurationFromUpdated(r.updatedAt)}
                        </td>
                      </tr>
                      );
                    })}
                    {(!data?.liveAssigned || data.liveAssigned.length === 0) && (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', color: '#757575' }}>
                          No active assigned pickings.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="pld-panel">
              <div className="pld-panel-title">LIVE PICKINGS - UNASSIGNED</div>
              <div className="pld-table-wrap">
                <table className="pld-table">
                  <thead>
                    <tr>
                      <th>DOC NO</th>
                      <th>CUST</th>
                      <th>ORDER TYPE</th>
                      <th>LINES</th>
                      <th>Issued</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.liveUnassigned || []).map((r) => {
                      const elapsed = elapsedMinutesSince(r.updatedAt);
                      const timeOver3h = elapsed != null && elapsed > LIVE_PICK_WARN_MINUTES;
                      return (
                      <tr key={r.id}>
                        <td>{r.docNo}</td>
                        <td>{r.cust}</td>
                        <td>{r.orderType}</td>
                        <td>{r.lineCount}</td>
                        <td>{formatIssued(r.issuedAt)}</td>
                        <td className={timeOver3h ? 'pld-time-over-threshold' : undefined}>
                          {formatDurationFromUpdated(r.updatedAt)}
                        </td>
                      </tr>
                      );
                    })}
                    {(!data?.liveUnassigned || data.liveUnassigned.length === 0) && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', color: '#757575' }}>
                          No unassigned pending lists.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </Layout>
  );
};

export default PickListDashboard;
